import { Routes } from '@angular/router';
import { authGuard } from '@guards/auth.guard';
import { devOnlyGuard } from '@guards/dev-only.guard';
import { featureFlagGuard } from '@guards/feature-flag.guard';
import { noAuthGuard } from '@guards/no-auth.guard';
import { permissionGuard } from '@guards/permission.guard';
import { NotFoundComponent } from '@shared/pages/not-found/not-found.component';
import { PaymentRequiredComponent } from '@shared/pages/payment-required/payment-required.component';
import { PaymentResultComponent } from '@shared/pages/payment-result/payment-result.component';
import { ConfigurationComponent } from './components/backoffice/configuration/configuration.component';
import { ProductDetailComponent } from './components/backoffice/configuration/products/product-detail/product-detail.component';
import { DashboardComponent } from './components/backoffice/dashboard/dashboard.component';
import { OrderComponent } from './components/backoffice/orders/order.component';
import { OrderDetailComponent } from './components/backoffice/orders/order-detail/order-detail.component';
import { RequestsComponent } from './components/backoffice/requests/requests.component';
import { SubscriptionListComponent } from './components/backoffice/subscriptions/subscription-list.component';
import { UsersComponent } from './components/backoffice/users/users.component';
import { BoostComponent } from './components/public/boost/boost.component';
import { LoginComponent } from './components/public/login/login.component';
import { RegisterComponent } from './components/public/register/register.component';
import { ResetPasswordComponent } from './components/public/reset-password/reset-password.component';
import { ResetPasswordConfirmComponent } from './components/public/reset-password-confirm/reset-password-confirm.component';
import { SubscriptionsComponent } from './components/public/subscriptions/subscriptions.component';
import { AuthLayoutComponent } from './layout/auth-layout/auth-layout.component';
import { MainLayoutComponent } from './layout/main-layout/main-layout.component';

export const routes: Routes = [
	// Auth pages - minimal layout, no navigation
	{
		path: 'login',
		component: AuthLayoutComponent,
		children: [{ path: '', component: LoginComponent, canActivate: [noAuthGuard] }],
	},
	{
		path: 'register',
		component: AuthLayoutComponent,
		children: [{ path: '', component: RegisterComponent, canActivate: [noAuthGuard] }],
	},
	{
		path: 'reset-password',
		component: AuthLayoutComponent,
		children: [{ path: '', component: ResetPasswordComponent, canActivate: [noAuthGuard] }],
	},
	{
		path: 'reset-password-confirm',
		component: AuthLayoutComponent,
		children: [{ path: '', component: ResetPasswordConfirmComponent }],
	},

	// All content pages - adaptive layout
	{
		path: '',
		component: MainLayoutComponent,
		children: [
			// Public (no auth required)
			{ path: '', redirectTo: 'boost', pathMatch: 'full' },
			{ path: 'boost', component: BoostComponent },
			{
				path: 'subscriptions',
				component: SubscriptionsComponent,
				canActivate: [authGuard(), featureFlagGuard('subscriptions')],
			},

			// Backoffice (auth required)
			{ path: 'orders', component: OrderComponent, canActivate: [authGuard()] },
			{ path: 'orders/:id', component: OrderDetailComponent, canActivate: [authGuard()] },
			{
				path: 'my-subscriptions',
				component: SubscriptionListComponent,
				canActivate: [authGuard(), featureFlagGuard('subscriptions')],
			},
			{
				path: 'dashboard',
				component: DashboardComponent,
				canActivate: [authGuard(), permissionGuard('page:view-dashboard')],
			},
			{
				path: 'products/:id',
				component: ProductDetailComponent,
				canActivate: [authGuard(), permissionGuard('page:view-products')],
			},
			{
				path: 'requests',
				component: RequestsComponent,
				canActivate: [authGuard(), permissionGuard('page:view-requests')],
			},
			{
				path: 'users',
				component: UsersComponent,
				canActivate: [authGuard(), permissionGuard('page:view-users')],
			},
			{
				path: 'configuration',
				component: ConfigurationComponent,
				canActivate: [authGuard(), permissionGuard('page:view-configuration')],
			},

			// Account
			{
				path: 'account',
				canActivate: [authGuard()],
				loadChildren: () => import('./components/backoffice/account/account.routes'),
			},
		],
	},

	// Standalone pages (no layout wrapper)
	// Dev-only component showcase: lazy, so it stays out of the production bundle
	{
		path: 'ui-kit',
		loadComponent: () => import('./components/public/ui-kit/ui-kit.component').then((m) => m.UiKitComponent),
		canActivate: [devOnlyGuard],
	},
	{ path: 'payment-required', component: PaymentRequiredComponent },
	{ path: 'payment-result', component: PaymentResultComponent },

	// URLs of the old site still indexed by Google (its result links /create-account): to the services page,
	// which has Log in and Sign up on top
	{ path: 'create-account', redirectTo: 'boost' },
	{ path: 'signup', redirectTo: 'boost' },
	{ path: 'sign-up', redirectTo: 'boost' },
	{ path: 'signin', redirectTo: 'login' },
	{ path: 'sign-in', redirectTo: 'login' },
	{ path: 'forgot-password', redirectTo: 'reset-password' },
	{ path: 'landing', redirectTo: 'boost' },
	{ path: 'home', redirectTo: 'boost' },

	// Catch-all
	{ path: '**', component: NotFoundComponent },
];
